import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const API_BASE_URL = ' https://ksa-manager-back.onrender.com';

export default function ScoutDecision() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scoutId = parseInt(id, 10);

  const [submittingStatus, setSubmittingStatus] = useState(null); // 'accepted' | 'denied'
  const [decision, setDecision] = useState(null); // 'accepted' | 'denied'

  const isSubmitting = submittingStatus !== null;

  const submitDecision = async (status) => {
    if (isSubmitting) return;

    if (!Number.isInteger(scoutId)) {
      toast.error('Invalid scout link.');
      return;
    }

    setSubmittingStatus(status);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/scout_questionnaires/offer_status`,
        {
          scout_id: scoutId,
          offer_status: status,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(
        response.data?.message || `Offer ${status} successfully`
      );

      setDecision(status);
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setSubmittingStatus(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 to-blue-900 px-4 py-10">
      <ToastContainer position="top-center" autoClose={3000} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
      >
        {decision ? (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              className={`mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full ${
                decision === 'accepted' ? 'bg-green-100' : 'bg-red-100'
              }`}
            >
              {decision === 'accepted' ? (
                <FaCheckCircle className="text-4xl text-green-500" />
              ) : (
                <FaTimesCircle className="text-4xl text-red-500" />
              )}
            </motion.div>
            <h1 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
              {decision === 'accepted' ? 'Offer Accepted' : 'Offer Denied'}
            </h1>
            <p className="text-sm text-gray-500 sm:text-base">
              Scout #{scoutId} offer has been {decision}.
            </p>
            <p className="mt-4 text-xs text-gray-400">
              Redirecting you to the website...
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <h1 className="mb-1 text-xl font-bold text-gray-900 sm:text-2xl">
                Scout Offer
              </h1>
              <p className="text-sm text-gray-500 sm:text-base">
                Please respond to the offer for Scout #{scoutId}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => submitDecision('accepted')}
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-500 px-5 py-3 text-base font-semibold text-white transition-all hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingStatus === 'accepted' ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaCheck />
                )}
                Accept
              </button>
              <button
                type="button"
                onClick={() => submitDecision('denied')}
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 px-5 py-3 text-base font-semibold text-white transition-all hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingStatus === 'denied' ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaTimes />
                )}
                Deny
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
